import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoggingService } from '@app/core/services';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { AuthService } from '@modules/auth/services';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { SKIP_GLOBAL_FORBIDDEN_REDIRECT } from './auth-interceptor.context';

// Header used by API-key authenticated endpoints (e.g. the file-queue upload
// service). The API hardens its scheme selection to require this explicit
// header and no longer treats a short/opaque bearer as an API key, so a request
// carrying this header must not also be given a user Bearer token.
const API_KEY_HEADER = 'X-Api-Key';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
    constructor(
        private _authService: AuthService,
        private _router: Router,
        private loggingService: LoggingService,
        private appSettingsStore: AppSettingsStore
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const apiRoots = [environment.apiRoot, environment.apiRootPython]
            .map((root) => root?.trim())
            .filter((root): root is string => !!root);
        const isProtectedApiRequest = apiRoots.some((root) => req.url.startsWith(root));

        if (!isProtectedApiRequest) {
            return next.handle(req);
        }

        // API-key authenticated requests (e.g. file-queue uploads) already carry
        // their own explicit `X-Api-Key` header. Do not attach a user Bearer /
        // tenant / user headers or run the session-renewal-and-logout flow for
        // them: mixing a Bearer with an API key is ambiguous for the hardened API
        // scheme selection, and an API-key failure must not trigger a user-session
        // logout/redirect storm.
        if (req.headers.has(API_KEY_HEADER)) {
            return next.handle(req);
        }

        const isServerLogRequest = req.url === environment.loggerConfig.serverLoggingUrl;

        return from(this._authService.getValidUser()).pipe(
            switchMap((user) => {
                if (!isServerLogRequest && this._authService.isSessionTerminationInProgress()) {
                    return throwError(() => this.createSessionUnavailableError(req));
                }

                const token = user?.access_token ?? '';

                if (isServerLogRequest && !(user && !user.expired)) {
                    return EMPTY;
                }

                // Never forward a protected request without a usable bearer.
                // Renewal may fail transiently, but the business operation must
                // stop at the client boundary instead of reaching the API and
                // producing a late task exception.
                if (!isServerLogRequest && (!user || user.expired || !token)) {
                    this.loggingService.warn('[AuthInterceptor] Protected request blocked because no valid session is available.');
                    // The AuthService defers this signal when its current
                    // access token is still live, so transient renewal failure
                    // does not force a logout. Once no live token remains, the
                    // normal single-flight logout flow is allowed to run.
                    this._authService.notifySessionExpired();
                    return throwError(() => this.createSessionUnavailableError(req));
                }

                if (!isServerLogRequest) {
                    if (user && !user.expired) {
                        if (token) {
                            this.loggingService.info('[AuthInterceptor] Bearer token is valid');
                        } else {
                            this.loggingService.warn('[AuthInterceptor] User logged in but token is missing');
                        }

                        const displayName =
                            user.profile?.given_name || user.profile?.name || user.profile?.preferred_username;
                        if (displayName) {
                            this.loggingService.info('[AuthInterceptor] User is not null:', displayName);
                        }
                    } else {
                        this.loggingService.warn('[AuthInterceptor] User is not logged in or token expired');
                    }
                }

                const tenantId = this.resolveTenantId(req);
                const userId = user?.profile?.sub?.trim() || user?.profile?.preferred_username?.trim() || user?.profile?.name?.trim() || '';
                const authReq = req.clone({
                    headers: this.buildHeaders(req, token, tenantId, userId),
                });

                return next.handle(authReq).pipe(
                    catchError((error) => {
                        const respError = error as HttpErrorResponse;

                        if (isServerLogRequest) {
                            return throwError(() => error);
                        }

                        if (respError?.status === 401) {
                            this.loggingService.warn('[AuthInterceptor] 401 detected. Attempting one session recovery...');

                            return from(this._authService.recoverFromUnauthorized(token)).pipe(
                                // This catch is intentionally before switchMap so it
                                // handles renewal only, not errors from the retried API.
                                catchError((renewError) => {
                                    this.loggingService.error('[AuthInterceptor] Token renewal failed. Signalling session expired.', renewError);
                                    this._authService.notifySessionExpired();
                                    return throwError(() => error);
                                }),
                                switchMap((newUser) => {
                                    if (newUser && !newUser.expired && newUser.access_token) {
                                        this.loggingService.info('[AuthInterceptor] Token renewed successfully. Retrying request...');

                                        const newUserId =
                                            newUser.profile?.sub?.trim() ||
                                            newUser.profile?.preferred_username?.trim() ||
                                            newUser.profile?.name?.trim() ||
                                            '';
                                        const retryReq = req.clone({
                                            headers: this.buildHeaders(req, newUser.access_token, tenantId, newUserId),
                                        });
                                        return next.handle(retryReq).pipe(
                                            catchError((retryError) => this.handleRetriedRequestError(retryError, req))
                                        );
                                    }

                                    this.loggingService.warn('[AuthInterceptor] Renewal succeeded but user invalid. Signalling session expired.');
                                    this._authService.notifySessionExpired();
                                    return throwError(() => error);
                                })
                            );
                        }

                        if (respError?.status === 403) {
                            this.handleForbidden(req);
                        }

                        return throwError(() => error);
                    })
                );
            })
        );
    }

    private handleRetriedRequestError(error: unknown, req: HttpRequest<any>): Observable<never> {
        const responseError = error as HttpErrorResponse;

        if (responseError?.status === 401) {
            this.loggingService.warn('[AuthInterceptor] Retried request remained unauthorized. Signalling session expired.');
            // A second 401 after a fresh-token retry is a confirmed server-side
            // authentication failure, so it may terminate the local session
            // even if the client-side exp claim has not reached its deadline.
            this._authService.notifySessionExpired();
        } else if (responseError?.status === 403) {
            this.handleForbidden(req);
        }

        return throwError(() => error);
    }

    private handleForbidden(req: HttpRequest<any>): void {
        const skipForbiddenRedirect = req.context.get(SKIP_GLOBAL_FORBIDDEN_REDIRECT);

        if (skipForbiddenRedirect) {
            this.loggingService.warn(
                '[AuthInterceptor] 403 received. Global unauthorized redirect skipped for request.',
                req.url
            );
            return;
        }

        this.loggingService.warn('[AuthInterceptor] Insufficient permissions, showing unauthorized page');
        void this._router.navigate(['/unauthorized']);
    }

    private createSessionUnavailableError(req: HttpRequest<any>): HttpErrorResponse {
        return new HttpErrorResponse({
            status: 401,
            statusText: 'Authentication session unavailable',
            url: req.url,
        });
    }

    private buildHeaders(req: HttpRequest<any>, token: string, tenantId: string, userId: string): HttpRequest<any>['headers'] {
        let headers = req.headers;

        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
            headers = tenantId
                ? headers.set('X-Tenant-Id', tenantId)
                : headers.delete('X-Tenant-Id');
            if (userId) {
                headers = headers.set('X-User', userId);
            }
        } else {
            // Do not forward a stale token or identity headers supplied by an
            // earlier interceptor when no valid application session exists.
            headers = headers
                .delete('Authorization')
                .delete('X-Tenant-Id')
                .delete('X-User');
        }

        return headers;
    }

    private resolveTenantId(req: HttpRequest<any>): string {
        const tenantId =
            req.headers.get('x-tenant-id')?.trim() ||
            this.appSettingsStore.settings()?.user?.clientId?.trim() ||
            '';

        return tenantId.toLowerCase() === 'system' ? '' : tenantId;
    }
}
